import { ExternalLink } from 'lucide-react';
import Image from 'next/image';
import NextLink from 'next/link';
import { useMemo } from 'react';

import { Badge, Tooltip } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';
import { getAddressScannerUrl, maskAddressValue } from '@/utils/format';

interface Props {
  address: string;
  chainId?: number;
  showNetworkIcon?: boolean;
  backgroundColor?: string;
  scannerUrl?: string;
  hideChainIcon?: boolean;
}

export function AddressChip({
  address,
  chainId,
  showNetworkIcon = true,
  backgroundColor,
  scannerUrl: customScannerUrl,
  hideChainIcon = false,
}: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const scannerUrl = useMemo(
    () => customScannerUrl || (chainId ? getAddressScannerUrl('chainId', chainId, address) : null),
    [customScannerUrl, chainId, address],
  );
  const chainIconUrl = useMemo(
    () => (showNetworkIcon && !hideChainIcon && chainId ? getChainIconUrl(chainId) : null),
    [chainId, getChainIconUrl, showNetworkIcon, hideChainIcon],
  );

  if (!scannerUrl) {
    return (
      <Badge variant="outline" size="sm" style={backgroundColor ? { backgroundColor } : undefined}>
        <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
          {chainIconUrl && <Image src={chainIconUrl} alt="network" width={16} height={16} />}
          <Tooltip content={address}>
            <span>{maskAddressValue(address)}</span>
          </Tooltip>
        </div>
      </Badge>
    );
  }

  return (
    <NextLink
      href={scannerUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="tw:flex tw:gap-0.5 tw:no-underline"
    >
      <Badge size="sm" style={backgroundColor ? { backgroundColor } : undefined}>
        <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
          {chainIconUrl && <Image src={chainIconUrl} alt="network" width={16} height={16} />}
          <Tooltip content={address}>
            <span>{maskAddressValue(address)}</span>
          </Tooltip>
          <ExternalLink size={12} />
        </div>
      </Badge>
    </NextLink>
  );
}
